import type { Firestore } from 'firebase-admin/firestore'
import type { 
  Clan, 
  ClanMember, 
  ClanMessage, 
  ClanPost, 
  ClanComment,
  ClanInvite,
  UserPresence,
  CreateClanDTO,
  UpdateClanDTO,
  SendMessageDTO,
  CreatePostDTO,
  CreateClanCommentDTO,
  UpdatePresenceDTO
} from '@my-roadtrip/types'
import { ClanRepository } from './clan.repository.js'
import { createLogger } from '../../common/utils/logger.js'
import { ValidationError, NotFoundError, ForbiddenError, ConflictError } from '../../common/errors/AppError.js'

const logger = createLogger('ClanService')

export class ClanService {
  private readonly repository: ClanRepository

  constructor(firestore: Firestore) {
    this.repository = new ClanRepository(firestore)
  }

  // ========================================
  // CLAN MANAGEMENT
  // ========================================

  async createClan(ownerId: string, data: CreateClanDTO, ownerProfile: { displayName: string; photoURL?: string }): Promise<Clan> {
    logger.info('Creating clan', { ownerId, clanName: data.name })
    // Validate name
    if (!data.name || data.name.trim().length < 3) {
      logger.warn('Invalid clan name', { ownerId, clanName: data.name })
      throw new ValidationError('Clan name must be at least 3 characters')
    }

    if (data.name.length > 50) {
      logger.warn('Clan name too long', { ownerId, clanName: data.name })
      throw new ValidationError('Clan name must be less than 50 characters')
    }

    const clan = await this.repository.createClan(ownerId, data, ownerProfile)
    logger.info('Clan created successfully', { clanId: clan.id, ownerId, clanName: clan.name })
    return clan
  }

  async getClan(clanId: string): Promise<Clan | null> {
    logger.debug('Fetching clan', { clanId })
    return this.repository.getClan(clanId)
  }

  async updateClan(clanId: string, userId: string, data: UpdateClanDTO): Promise<void> {
    logger.info('Updating clan', { clanId, userId, updateFields: Object.keys(data).length })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Check permissions (only owner/admin can update)
    const member = clan.members.find(m => m.userId === userId)
    if (!member || (member.role !== 'owner' && member.role !== 'admin')) {
      logger.warn('Insufficient permissions to update clan', { clanId, userId, memberRole: member?.role })
      throw new ForbiddenError('Insufficient permissions')
    }

    await this.repository.updateClan(clanId, data)
    logger.info('Clan updated successfully', { clanId, userId })
  }

  async deleteClan(clanId: string, userId: string): Promise<void> {
    logger.info('Deleting clan', { clanId, userId })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found for deletion', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Only owner can delete
    if (clan.ownerId !== userId) {
      logger.warn('Only owner can delete clan', { clanId, userId, clanOwnerId: clan.ownerId })
      throw new ForbiddenError('Only clan owner can delete clan')
    }

    await this.repository.deleteClan(clanId)
    logger.info('Clan deleted successfully', { clanId, userId })
  }

  async getUserClans(userId: string): Promise<Clan[]> {
    logger.debug('Fetching user clans', { userId })
    return this.repository.getUserClans(userId)
  }

  async searchClans(query: string): Promise<Clan[]> {
    logger.debug('Searching clans', { query: query.substring(0, 50) })
    return this.repository.searchClans(query)
  }

  // ========================================
  // MEMBER MANAGEMENT
  // ========================================

  async joinClan(clanId: string, userId: string, userProfile: { displayName: string; photoURL?: string }): Promise<void> {
    logger.info('Joining clan', { clanId, userId })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Check if already member
    if (clan.members.some(m => m.userId === userId)) {
      logger.warn('User already member of clan', { clanId, userId })
      throw new ConflictError('Already a member')
    }

    // Check if clan is full
    if (clan.members.length >= clan.maxMembers) {
      logger.warn('Clan is full', { clanId, memberCount: clan.members.length, maxMembers: clan.maxMembers })
      throw new ConflictError('Clan is full')
    }

    // Check visibility
    if (clan.visibility === 'private' || clan.visibility === 'invite-only') {
      logger.warn('Clan requires invitation', { clanId, userId, visibility: clan.visibility })
      throw new ForbiddenError('This clan requires an invitation')
    }

    const member: ClanMember = {
      userId,
      displayName: userProfile.displayName,
      photoURL: userProfile.photoURL,
      role: 'member',
      joinedAt: new Date(),
      isOnline: false
    }

    await this.repository.addMember(clanId, member)
    logger.info('User joined clan successfully', { clanId, userId })
  }

  async leaveClan(clanId: string, userId: string): Promise<void> {
    logger.info('Leaving clan', { clanId, userId })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Owner cannot leave (must transfer ownership or delete clan)
    if (clan.ownerId === userId) {
      logger.warn('Owner cannot leave clan', { clanId, userId })
      throw new ForbiddenError('Owner cannot leave clan. Transfer ownership or delete clan.')
    }

    await this.repository.removeMember(clanId, userId)
    logger.info('User left clan', { clanId, userId })
  }

  async kickMember(clanId: string, requesterId: string, targetUserId: string): Promise<void> {
    logger.info('Kicking member from clan', { clanId, requesterId, targetUserId })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Check requester permissions
    const requester = clan.members.find(m => m.userId === requesterId)
    if (!requester || (requester.role !== 'owner' && requester.role !== 'admin')) {
      logger.warn('Insufficient permissions to kick member', { clanId, requesterId, role: requester?.role })
      throw new ForbiddenError('Insufficient permissions')
    }

    // Cannot kick owner
    if (clan.ownerId === targetUserId) {
      logger.warn('Cannot kick clan owner', { clanId, targetUserId })
      throw new ForbiddenError('Cannot kick clan owner')
    }

    // Admins cannot kick other admins
    const target = clan.members.find(m => m.userId === targetUserId)
    if (target?.role === 'admin' && requester.role === 'admin') {
      logger.warn('Admins cannot kick other admins', { clanId, requesterId, targetUserId })
      throw new ForbiddenError('Admins cannot kick other admins')
    }

    await this.repository.removeMember(clanId, targetUserId)
    logger.info('Member kicked from clan', { clanId, targetUserId })
  }

  async updateMemberRole(clanId: string, requesterId: string, targetUserId: string, newRole: 'admin' | 'member'): Promise<void> {
    logger.info('Updating member role', { clanId, requesterId, targetUserId, newRole })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Only owner can change roles
    if (clan.ownerId !== requesterId) {
      logger.warn('Only owner can change member roles', { clanId, requesterId })
      throw new ForbiddenError('Only clan owner can change member roles')
    }

    // Cannot change owner role
    if (clan.ownerId === targetUserId) {
      logger.warn('Cannot change owner role', { clanId, targetUserId })
      throw new ForbiddenError('Cannot change owner role')
    }

    await this.repository.updateMemberRole(clanId, targetUserId, newRole)
    logger.info('Member role updated', { clanId, targetUserId, newRole })
  }

  // ========================================
  // CHAT MESSAGES
  // ========================================

  async sendMessage(clanId: string, userId: string, userProfile: { displayName: string; photoURL?: string }, data: SendMessageDTO): Promise<ClanMessage> {
    logger.debug('Sending clan message', { clanId, userId, messageType: data.type })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      logger.warn('User not a clan member', { clanId, userId })
      throw new ForbiddenError('Not a clan member')
    }

    // Check if chat is enabled
    if (!clan.settings?.allowChat) {
      logger.warn('Chat disabled for clan', { clanId })
      throw new ForbiddenError('Chat is disabled for this clan')
    }

    const message: Omit<ClanMessage, 'id'> = {
      clanId,
      userId,
      userName: userProfile.displayName,
      userPhotoURL: userProfile.photoURL,
      type: data.type || 'text',
      content: data.content,
      imageURL: data.imageURL,
      fileURL: data.fileURL,
      fileName: data.fileName,
      location: data.location,
      timestamp: new Date()
    }

    const result = await this.repository.createMessage(message)
    logger.info('Clan message sent', { clanId, userId, messageId: result.id })
    return result
  }

  async getMessages(clanId: string, userId: string, limit = 50): Promise<ClanMessage[]> {
    logger.debug('Fetching clan messages', { clanId, userId, limit })
    const clan = await this.repository.getClan(clanId)
    if (!clan) {
      logger.warn('Clan not found', { clanId })
      throw new NotFoundError('Clan not found')
    }

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      logger.warn('User not a clan member', { clanId, userId })
      throw new ForbiddenError('Not a clan member')
    }

    return this.repository.getMessages(clanId, limit)
  }

  async deleteMessage(clanId: string, messageId: string, userId: string): Promise<void> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check permissions (message author, admin, or owner)
    const member = clan.members.find(m => m.userId === userId)
    if (!member) throw new ForbiddenError('Not a clan member')

    // Get message to check author
    const messages = await this.repository.getMessages(clanId, 1)
    const message = messages.find(m => m.id === messageId)
    
    if (!message) throw new NotFoundError('Message not found')

    if (message.userId !== userId && member.role !== 'admin' && member.role !== 'owner') {
      throw new ForbiddenError('Insufficient permissions')
    }

    await this.repository.deleteMessage(clanId, messageId)
  }

  // ========================================
  // FEED POSTS
  // ========================================

  async createPost(clanId: string, userId: string, userProfile: { displayName: string; photoURL?: string }, data: CreatePostDTO): Promise<ClanPost> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    // Check if posts are enabled
    if (!clan.settings?.allowPosts) {
      throw new ForbiddenError('Posts are disabled for this clan')
    }

    const post: Omit<ClanPost, 'id' | 'likeCount' | 'commentCount' | 'likedBy' | 'isPinned'> = {
      clanId,
      userId,
      userName: userProfile.displayName,
      userPhotoURL: userProfile.photoURL,
      type: data.type,
      visibility: data.visibility || 'clan',
      content: data.content,
      title: data.title,
      images: data.images,
      tripId: data.tripId,
      poll: data.poll ? { ...data.poll, votes: {} } : undefined,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    return this.repository.createPost(post as any)
  }

  async getPosts(clanId: string, userId: string, limit = 20): Promise<ClanPost[]> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    return this.repository.getPosts(clanId, limit)
  }

  async likePost(clanId: string, postId: string, userId: string): Promise<void> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    await this.repository.likePost(clanId, postId, userId)
  }

  async deletePost(clanId: string, postId: string, userId: string): Promise<void> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    const member = clan.members.find(m => m.userId === userId)
    if (!member) throw new ForbiddenError('Not a clan member')

    const post = await this.repository.getPost(clanId, postId)
    if (!post) throw new NotFoundError('Post not found')

    // Only post author, admin, or owner can delete
    if (post.userId !== userId && member.role !== 'admin' && member.role !== 'owner') {
      throw new ForbiddenError('Insufficient permissions')
    }

    await this.repository.deletePost(clanId, postId)
  }

  // ========================================
  // COMMENTS
  // ========================================

  async createComment(clanId: string, postId: string, userId: string, userProfile: { displayName: string; photoURL?: string }, data: CreateClanCommentDTO): Promise<ClanComment> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    const comment: Omit<ClanComment, 'id' | 'likeCount' | 'likedBy'> = {
      postId,
      clanId,
      userId,
      userName: userProfile.displayName,
      userPhotoURL: userProfile.photoURL,
      content: data.content,
      imageURL: data.imageURL,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    return this.repository.createComment(clanId, postId, comment as any)
  }

  async getComments(clanId: string, postId: string, userId: string): Promise<ClanComment[]> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    return this.repository.getComments(clanId, postId)
  }

  async deleteComment(clanId: string, postId: string, commentId: string, userId: string): Promise<void> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    const member = clan.members.find(m => m.userId === userId)
    if (!member) throw new ForbiddenError('Not a clan member')

    // Get comment to check author
    const comments = await this.repository.getComments(clanId, postId)
    const comment = comments.find(c => c.id === commentId)
    
    if (!comment) throw new NotFoundError('Comment not found')

    // Only comment author, admin, or owner can delete
    if (comment.userId !== userId && member.role !== 'admin' && member.role !== 'owner') {
      throw new ForbiddenError('Insufficient permissions')
    }

    await this.repository.deleteComment(clanId, postId, commentId)
  }

  // ========================================
  // INVITES
  // ========================================

  async inviteUser(clanId: string, inviterId: string, inviteeId: string): Promise<ClanInvite> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if inviter is member with permission
    const inviter = clan.members.find(m => m.userId === inviterId)
    if (!inviter) throw new ForbiddenError('Not a clan member')

    if (!clan.settings?.allowInvites && inviter.role === 'member') {
      throw new ForbiddenError('Only admins can invite users')
    }

    // Check if invitee is already member
    if (clan.members.some(m => m.userId === inviteeId)) {
      throw new ConflictError('User is already a member')
    }

    // Check if clan is full
    if (clan.members.length >= clan.maxMembers) {
      throw new ConflictError('Clan is full')
    }

    const invite: Omit<ClanInvite, 'id'> = {
      clanId,
      inviterId,
      inviteeId,
      status: 'pending',
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
    }

    return this.repository.createInvite(invite)
  }

  async acceptInvite(inviteId: string, userId: string, userProfile: { displayName: string; photoURL?: string }): Promise<void> {
    const invite = await this.repository.getInvite(inviteId)
    if (!invite) throw new NotFoundError('Invite not found')

    if (invite.inviteeId !== userId) {
      throw new ForbiddenError('This invite is not for you')
    }

    if (invite.status !== 'pending') {
      throw new ValidationError('Invite is no longer valid')
    }

    if (new Date() > invite.expiresAt) {
      await this.repository.updateInviteStatus(inviteId, 'declined')
      throw new ValidationError('Invite has expired')
    }

    const clan = await this.repository.getClan(invite.clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if clan is full
    if (clan.members.length >= clan.maxMembers) {
      throw new ConflictError('Clan is full')
    }

    const member: ClanMember = {
      userId,
      displayName: userProfile.displayName,
      photoURL: userProfile.photoURL,
      role: 'member',
      joinedAt: new Date(),
      isOnline: false
    }

    await this.repository.addMember(invite.clanId, member)
    await this.repository.updateInviteStatus(inviteId, 'accepted')
  }

  async declineInvite(inviteId: string, userId: string): Promise<void> {
    const invite = await this.repository.getInvite(inviteId)
    if (!invite) throw new NotFoundError('Invite not found')

    if (invite.inviteeId !== userId) {
      throw new ForbiddenError('This invite is not for you')
    }

    await this.repository.updateInviteStatus(inviteId, 'declined')
  }

  async getUserInvites(userId: string): Promise<ClanInvite[]> {
    return this.repository.getUserInvites(userId)
  }

  // ========================================
  // PRESENCE
  // ========================================

  async updatePresence(clanId: string, userId: string, data: UpdatePresenceDTO): Promise<void> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    const presence: UserPresence = {
      userId,
      clanId,
      status: data.status,
      lastSeen: new Date(),
      currentActivity: data.currentActivity
    }

    await this.repository.updatePresence(presence)
  }

  async getOnlineMembers(clanId: string, userId: string): Promise<UserPresence[]> {
    const clan = await this.repository.getClan(clanId)
    if (!clan) throw new NotFoundError('Clan not found')

    // Check if user is member
    if (!clan.members.some(m => m.userId === userId)) {
      throw new ForbiddenError('Not a clan member')
    }

    return this.repository.getPresence(clanId)
  }
}
